import React from 'react';
import { ExternalLink, Copy, MessageCircle, Calendar } from 'lucide-react';
import { Assistant, AssistantStats } from '../../types';
import { getCurrentUrlPrefix } from '../../utils/helpers';

interface YourEchooPageProps {
    assistant: Assistant;
    stats: AssistantStats;
    personalContextEntries: any[];
    backgroundImageUrl?: string;
    onNavigateToBuildStory: () => void;
}

const YourEchooPage: React.FC<YourEchooPageProps> = ({
    assistant,
    stats,
    personalContextEntries,
    backgroundImageUrl,
    onNavigateToBuildStory
}) => {
    const echooUrl = `${getCurrentUrlPrefix()}${assistant.unique_url}`;
    const isSetupComplete = personalContextEntries.length > 0;

    const copyToClipboard = () => {
        navigator.clipboard.writeText(echooUrl);
        // You might want to add a toast notification here
    };

    return (
        <div className="space-y-8 max-w-4xl">
            {/* Main Echoo Card */}
            <div className="relative">
                <div
                    className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 border border-slate-200/50 overflow-hidden relative"
                    style={backgroundImageUrl ? {
                        backgroundImage: `linear-gradient(rgba(248, 250, 252, 0.9), rgba(239, 246, 255, 0.9)), url(${backgroundImageUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    } : {}}
                >
                    <div className="relative z-10">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                                    Your Echoo is {isSetupComplete ? 'ready!' : 'almost ready!'}
                                </h1>
                                <p className="text-lg text-slate-600">
                                    {isSetupComplete
                                        ? 'People can now chat with your personal AI assistant'
                                        : 'Complete your story to make it even better'
                                    }
                                </p>
                            </div>
                        </div>

                        {/* Echoo Preview */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/50 mb-6">
                            <div className="flex items-center mb-4">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg mr-4"
                                    style={{ backgroundColor: assistant.primary_color }}
                                >
                                    {assistant.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-slate-900">{assistant.name}</h3>
                                    <p className="text-slate-600">{assistant.pronouns}</p>
                                </div>
                            </div>
                            <div className="bg-slate-50 rounded-lg p-4">
                                <p className="text-slate-700">
                                    "Hi! I'm {assistant.name}'s personal Echoo. I can answer questions about {assistant.pronouns === 'they/them' ? 'their' : assistant.pronouns.includes('she') ? 'her' : 'his'} background, experience, and interests. What would you like to know?"
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <button
                                onClick={() => window.open(echooUrl, '_blank')}
                                className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl transform hover:-translate-y-0.5 transition-all duration-200 shadow-lg"
                            >
                                <MessageCircle className="w-5 h-5 mr-2" />
                                Try Your Echoo
                            </button>
                            {!isSetupComplete && (
                                <button
                                    onClick={onNavigateToBuildStory}
                                    className="flex items-center justify-center px-6 py-4 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 transition-colors"
                                >
                                    Complete Your Story
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Share Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/50">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">Share Your Echoo</h2>

                {/* URL */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Echoo URL</label>
                    <div className="flex items-center space-x-3">
                        <div className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg font-mono text-slate-700">
                            {echooUrl}
                        </div>
                        <button
                            onClick={copyToClipboard}
                            className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                            title="Copy URL"
                        >
                            <Copy className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => window.open(echooUrl, '_blank')}
                            className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                            title="Open in new tab"
                        >
                            <ExternalLink className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/50 text-center">
                    <div className="text-2xl font-bold text-slate-900 mb-1">{stats.totalConversations || 0}</div>
                    <div className="text-sm text-slate-600">Conversations</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/50 text-center">
                    <div className="text-2xl font-bold text-slate-900 mb-1">{stats.totalexchanges || 0}</div>
                    <div className="text-sm text-slate-600">Messages</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/50 text-center">
                    <div className="text-2xl font-bold text-slate-900 mb-1">
                        {personalContextEntries.length}
                    </div>
                    <div className="text-sm text-slate-600">Story Sections</div>
                </div>
            </div>

            {/* Setup Status */}
            {!isSetupComplete && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2">Complete Your Echoo</h3>
                    <p className="text-orange-800 mb-4">
                        Add personal context to help your Echoo provide better, more personalized responses.
                    </p>
                    <button
                        onClick={onNavigateToBuildStory}
                        className="px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
                    >
                        Add Your Story
                    </button>
                </div>
            )}
        </div>
    );
};

export default YourEchooPage;
